import { Api } from '../api';
import {
  CustomerFunnelResponse,
  CustomerFunnelRequest,
  CustomerFunnelGroupsRequest,
  CustomerFunnelGroupsResponse,
  UpdateCustomerFunnelGroupRequest,
  UpdateCustomerFunnelGroupResponse,
  GetAvailableGroupsResponse,
  GetAvailableGroupsRequest,
  InitalizeFunnelsRequest,
  GetFunnelGroupMembersResponse,
  GetFunnelGroupMembersRequest,
  UpdateNewFunnelResponse,
  UpdateNewFunnelRequest,
  DeleteFunnelRequest,
  GetMetaConversationalDetailsRequest,
  GetMetaConversationalDetailsResponse,
  UpdateMetaConversationalDetailsRequest,
  GetQueryTypesResponse,
  GetQueryTypesRequest,
  AddQueryTypeRequest,
  AddQueryTypeResponse,
  UpdateQueryTypeRequest,
  UpdateQueryTypeResponse,
  DeleteQueryTypeRequest,
  DeleteQueryTypeResponse,
  GetQueryTypesListRequest,
  GetQueryTypesListResponse,
  GetConversationTypesResponse,
  GetConversationTypesRequest,
  GetQueryStatsResponse,
  GetQueryStatsRequest,
  GetWorkspaceFiltersRequest,
  GetWorkspaceFiltersResponse,
  GetChannelThreadsListRequest,
  GetChannelThreadsListResponse,
  GetQueryTypeActionsRequest,
  GetQueryTypeActionsResponse,
} from './model';

export const growDashboardApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    getCustomerFunnels: builder.query<
      CustomerFunnelResponse,
      CustomerFunnelRequest
    >({
      query(arg) {
        const { sellerWorkspaceId } = arg;
        return {
          url: `/hub/orgs/api/workspaces/${sellerWorkspaceId}/funnels`,
          method: 'GET',
          params: arg,
        };
      },
    }),
    getCustomerFunnelGroups: builder.mutation<
      CustomerFunnelGroupsResponse,
      CustomerFunnelGroupsRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/workspaces/${arg.sellerWorkspaceId}/funnels/groups`,
          method: 'GET',
          params: { ...arg.params, isReturnNewSemanticConversations: true },
        };
      },
    }),
    updateCustomerFunnelGroup: builder.mutation<
      UpdateCustomerFunnelGroupResponse,
      UpdateCustomerFunnelGroupRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/workspaces/${arg.params.sellerWorkspaceId}/funnels`,
          method: 'PUT',
          params: arg.params,
          body: arg.body,
        };
      },
    }),
    getAvailableGroups: builder.query<
      GetAvailableGroupsResponse,
      GetAvailableGroupsRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/workspaces/${arg.params.sellerWorkspaceId}/funnels/${arg.funnelId}/available-groups`,
          method: 'GET',
          params: arg.params,
        };
      },
    }),
    initialzeFunnel: builder.mutation<any, InitalizeFunnelsRequest>({
      query(arg) {
        return {
          url: `/hub/orgs/api/workspaces/${arg.params.sellerWorkspaceId}/funnels/initialize`,
          method: 'POST',
          params: arg.params,
        };
      },
    }),
    getFunnelGroupMembers: builder.mutation<
      GetFunnelGroupMembersResponse,
      GetFunnelGroupMembersRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/customer-group/conversations/${arg.groupId}`,
          method: 'POST',
          params: arg.params,
          body: {
            ...arg.body,
            isReturnNewSemanticConversations: true,
          },
        };
      },
    }),
    updateFunnel: builder.mutation<
      UpdateNewFunnelResponse,
      UpdateNewFunnelRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/workspaces/${arg.params.sellerWorkspaceId}/funnels`,
          method: 'POST',
          params: arg.params,
          body: arg.body,
        };
      },
    }),
    deleteFunnel: builder.mutation<any, DeleteFunnelRequest>({
      query(arg) {
        return {
          url: `/hub/orgs/api/workspaces/${arg.params.sellerWorkspaceId}/funnels/${arg.funnelId}`,
          method: 'DELETE',
          params: arg.params,
        };
      },
    }),
    getMetaConversationalDetails: builder.query<
      GetMetaConversationalDetailsResponse,
      GetMetaConversationalDetailsRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/ad-setup/${arg.params.sellerWorkspaceId}/META`,
          method: 'GET',
          params: arg.params,
        };
      },
    }),
    updateMetaConversationalDetails: builder.mutation<
      any,
      UpdateMetaConversationalDetailsRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/ad-setup/${arg.params.sellerWorkspaceId}`,
          method: 'POST',
          params: arg.params,
          body: arg.body,
        };
      },
    }),
    getQueryTypes: builder.query<GetQueryTypesResponse, GetQueryTypesRequest>({
      query(args) {
        return {
          url: `/hub/communication-v2/api/conversation-threads/query-types/${args.params.sellerWorkspaceId}`,
          method: 'GET',
          params: args.params,
        };
      },
    }),
    getConversationType: builder.query<
      GetConversationTypesResponse,
      GetConversationTypesRequest
    >({
      query(args) {
        return {
          url: `/hub/communication-v2/api/conversation-threads/${args.params.sellerWorkspaceId}`,
          method: 'GET',
          params: args.params,
        };
      },
    }),
    getQueryStats: builder.query<GetQueryStatsResponse, GetQueryStatsRequest>({
      query(args) {
        return {
          url: `/hub/communication-v2/api/conversation-threads/stats/${args.params.sellerWorkspaceId}`,
          method: 'GET',
          params: args.params,
        };
      },
    }),
    getWorkspaceFilters: builder.query<
      GetWorkspaceFiltersResponse,
      GetWorkspaceFiltersRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/workspaces/filters/${arg.funnelId}`,
          method: 'GET',
          params: arg.params,
        };
      },
    }),
    addQueryType: builder.mutation<AddQueryTypeResponse, AddQueryTypeRequest>({
      query(arg) {
        return {
          url: `/hub/communication-v2/api/conversation-threads/query-type?sellerWorkspaceId=${arg.workspaceId}`,
          method: 'POST',
          body: {
            workspaceId: arg.workspaceId,
            queryType: arg.queryType,
            color: arg.color,
            icon: arg.icon,
            prompt: arg.prompt,
            isLlmGenerated: arg.isLlmGenerated,
            actions: arg.actions,
          },
        };
      },
    }),
    updateQueryType: builder.mutation<
      UpdateQueryTypeResponse,
      UpdateQueryTypeRequest
    >({
      query(arg) {
        return {
          url: `/hub/communication-v2/api/conversation-threads/query-type?sellerWorkspaceId=${arg.workspaceId}`,
          method: 'PATCH',
          body: {
            workspaceId: arg.workspaceId,
            queryType: arg.queryType,
            newQueryType: arg.newQueryType,
            color: arg.color,
            icon: arg.icon,
            prompt: arg.prompt,
            actions: arg.actions,
          },
        };
      },
    }),
    deleteQueryType: builder.mutation<
      DeleteQueryTypeResponse,
      DeleteQueryTypeRequest
    >({
      query(arg) {
        return {
          url: `/hub/communication-v2/api/conversation-threads/query-type?sellerWorkspaceId=${arg.workspaceId}`,
          method: 'DELETE',
          body: {
            workspaceId: arg.workspaceId,
            queryType: arg.queryType,
          },
        };
      },
    }),
    getQueryTypesList: builder.query<
      GetQueryTypesListResponse,
      GetQueryTypesListRequest
    >({
      query(args) {
        return {
          url: `/hub/communication-v2/api/conversation-threads/query-types/list/${args.workspaceId}`,
          method: 'GET',
          params: args.params,
        };
      },
    }),
    getChannelThreadsList: builder.query<
      GetChannelThreadsListResponse,
      GetChannelThreadsListRequest
    >({
      query(args) {
        return {
          url: `/hub/communication-v2/api/conversation-threads/list/channels/${args.workspaceId}`,
          method: 'GET',
          params: args.params,
        };
      },
    }),
    getQueryTypeActions: builder.query<
      GetQueryTypeActionsResponse,
      GetQueryTypeActionsRequest
    >({
      query(arg) {
        return {
          url: `/hub/communication-v2/api/conversation-threads/actions/${arg.workspaceId}`,
          method: 'GET',
          params: { sellerWorkspaceId: arg.workspaceId },
        };
      },
    }),
  }),
});

export const {
  useLazyGetCustomerFunnelsQuery,
  useGetCustomerFunnelGroupsMutation,
  useUpdateCustomerFunnelGroupMutation,
  useLazyGetAvailableGroupsQuery,
  // useLazyGetFunnelGroupCustomerConversationsQuery,
  useInitialzeFunnelMutation,
  useGetFunnelGroupMembersMutation,
  useUpdateFunnelMutation,
  useDeleteFunnelMutation,
  useLazyGetMetaConversationalDetailsQuery,
  useUpdateMetaConversationalDetailsMutation,
  useLazyGetQueryTypesQuery,
  useLazyGetConversationTypeQuery,
  useLazyGetQueryStatsQuery,
  useLazyGetWorkspaceFiltersQuery,
  useAddQueryTypeMutation,
  useUpdateQueryTypeMutation,
  useDeleteQueryTypeMutation,
  useLazyGetQueryTypesListQuery,
  useLazyGetChannelThreadsListQuery,
  useLazyGetQueryTypeActionsQuery,
} = growDashboardApi;
